import React from "react";
import { Box, Paper, Typography, Button, Chip, alpha } from "@mui/material";
import { ThemeProvider, createTheme } from "@mui/material/styles";
import DirectionsCarFilledOutlinedIcon from "@mui/icons-material/DirectionsCarFilledOutlined";
import { brandColors, brandThemeOptions } from "./brandColors";
import HouseOutlinedIcon from '@mui/icons-material/HouseOutlined';
import ShieldOutlinedIcon from '@mui/icons-material/ShieldOutlined';
import PersonOutlinedIcon from '@mui/icons-material/PersonOutlined';
interface PolicyAction {
    id: string;
    label: string;
    params?: any;
    followup_message?: string;
}

interface PolicyDetailsCardProps {
    card: any;
    onAction?: (action: PolicyAction) => void;
}

const getItemValue = (items: any[], label: string) => {
    const found = (items || []).find((i) => (i?.label || "").toLowerCase() === label.toLowerCase());
    return found ? found.value : undefined;
};

const getItemValueFromLabels = (items: any[], labels: string[]) => {
    for (const label of labels) {
        const value = getItemValue(items, label);
        if (value !== undefined && value !== null && value !== "") return value;
    }
    return undefined;
};

const PolicyDetailsCard: React.FC<PolicyDetailsCardProps> = ({ card, onAction }) => {
    if (!card) return null;

    const normalizedCard = card?.card || card;

    // Expect first section to contain the scalar policy details
    const sections = normalizedCard.sections || [];
    const detailsSection = sections.find((s: any) => (s?.heading || "").toLowerCase().includes("policy")) || sections[0];
    const termSection = sections.find((s: any) => (s?.heading || "").toLowerCase().includes("term"));
    const vehiclesSection = sections.find((s: any) => (s?.heading || "").toLowerCase().includes("vehicle"));
    const driversSection = sections.find((s: any) => (s?.heading || "").toLowerCase().includes("driver"));
    const propertySection = sections.find((s: any) => (s?.heading || "").toLowerCase().includes("property"));
    const coveragesSection = sections.find((s: any) => (s?.heading || "").toLowerCase().includes("coverages"));

    const detailsItems = detailsSection?.items || [];
    const termItems = termSection?.items || [];

    const premium = getItemValueFromLabels(detailsItems, ["Premium"]);
    const dueAmount = getItemValueFromLabels(detailsItems, ["Due Amount"]);
    const effective = getItemValueFromLabels([...detailsItems, ...termItems], ["Effective", "Start Date"]);
    const expires = getItemValueFromLabels([...detailsItems, ...termItems], ["Expires", "End Date"]);
    const dueDate = getItemValueFromLabels(detailsItems, ["Due Date"]);
    const status = getItemValueFromLabels(detailsItems, ["Status"]);

    const handleAction = (action?: PolicyAction) => {
        if (!action) return;
        onAction?.(action);
    };

    const handleCtaAction = (action: PolicyAction) => {
        if (onAction) onAction(action);
    };

    const renderSection = (
  section: any,
  title: string,
  icon: React.ReactNode
) => {
  if (!section) return null;

  return (
    <Box sx={{ borderTop: "1px solid #e5e7eb", mt: 1.25, pt: 1 }}>
      <Typography
        sx={{
          fontWeight: 700,
          fontSize: { xs: 11, md: 12 },
          color: "#374151",
          mb: 0.5,
        }}
      >
        {title}
      </Typography>

      <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
        {(section.items || []).map((item: any, idx: number) => (
          <Box
            key={`${title}_${idx}`}
            sx={{
              position: "relative",
              overflow: "hidden",
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              gap: 1,
              border: "1px solid #eceff3",
              borderRadius: { xs: 2, md: 2.5 },
              px: { xs: 1, md: 1.5 },
              pl: { xs: 1.5, md: 1.75 },
              py: { xs: 0.75, md: 1 },
              transition:
                "background-color 150ms ease, border-color 200ms ease, box-shadow 200ms ease",
              "&::before": {
                content: '""',
                position: "absolute",
                left: 0,
                top: 0,
                bottom: 0,
                width: "3px",
                bgcolor: "primary.main",
              },
              "&:hover": {
                bgcolor: (theme) => alpha(theme.palette.primary.main, 0.05),
                borderColor: (theme) => alpha(theme.palette.primary.main, 0.4),
              },
            }}
          >
            <Box sx={{ display: "flex", alignItems: "center", gap: 1, minWidth: 0 }}>
              <Box
                sx={{
                  width: { xs: 28, md: 32 },
                  height: { xs: 28, md: 32 },
                  borderRadius: "9px",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  color: "primary.main",
                  bgcolor: (theme) => alpha(theme.palette.primary.main, 0.12),
                  flexShrink: 0,
                  "& .MuiSvgIcon-root": { fontSize: { xs: 17, md: 19 } },
                }}
              >
                {icon}
              </Box>
              <Box sx={{ minWidth: 0 }}>
                <Typography
                  sx={{ fontWeight: 700, fontSize: { xs: 12, md: 14 }, color: "#1f2937" }}
                >
                  {item?.label}
                </Typography>
                {item?.value && (
                  <Typography
                    sx={{ fontSize: { xs: 10, md: 12 }, color: "#6b7280" }}
                  >
                    {item.value}
                  </Typography>
                )}
              </Box>
            </Box>

            {item?.action && (
              <Button
                size="small"
                variant="outlined"
                onClick={(e) => {
                  e.stopPropagation();
                  handleAction(item.action);
                }}
                sx={{
                  textTransform: "none",
                  fontSize: { xs: 10, md: 12 },
                  fontWeight: 600,
                  borderRadius: 9999,
                  px: { xs: 1.25, md: 1.5 },
                  whiteSpace: "nowrap",
                //   borderColor: (theme) => alpha(theme.palette.primary.main, 0.5),
                //   color: "primary.main",
                //   "&:hover": {
                //     borderColor: "primary.main",
                //     bgcolor: (theme) => alpha(theme.palette.primary.main, 0.08),
                //   },
                }}
              >
                {item.action?.label || "View"}
              </Button>
            )}
          </Box>
        ))}
      </Box>
    </Box>
  );
};

    return (
        <ThemeProvider theme={(outer) => createTheme(outer, brandThemeOptions)}>
        <Paper
            elevation={0}
            sx={{
                width: { xs: "100%", md: "100%" },
                border: "1px solid #eceff3",
                borderRadius: { xs: 2, md: 2.5 },
                p: { xs: 1.25, md: 1.75 },
                bgcolor: "#ffffff",
            }}
        >
            <Box sx={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between", gap: 1 }}>
                <Box sx={{ display: "flex", alignItems: "center", gap: 1, minWidth: 0 }}>
                    <Box
                        sx={{
                            width: { xs: 28, md: 32 },
                            height: { xs: 28, md: 32 },
                            borderRadius: "9px",
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            color: "primary.main",
                            bgcolor: (theme) => alpha(theme.palette.primary.main, 0.12),
                            flexShrink: 0,
                        }}
                    >
                        <ShieldOutlinedIcon sx={{ fontSize: { xs: 17, md: 19 } }} />
                    </Box>
                    <Box sx={{ minWidth: 0 }}>
                        <Typography sx={{ fontWeight: 700, fontSize: { xs: 12.5, md: 14 }, color: "#111827" }}>
                            {normalizedCard?.title || "Policy Details"}
                        </Typography>
                        {normalizedCard?.subtitle && (
                            <Typography sx={{ fontSize: { xs: 10.5, md: 12 }, color: "#6b7280" }}>
                                {normalizedCard.subtitle}
                            </Typography>
                        )}
                    </Box>
                </Box>
                {status && (
                    <Chip
                        label={status}
                        color="success"
                        size="small"
                        sx={{ fontWeight: 600, borderRadius: 9999, flexShrink: 0 }}
                    />
                )}
            </Box>

            <Box
                sx={{
                    display: "grid",
                    gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr" },
                    gap: { xs: 1, md: 1.5 },
                    mt: 1,
                }}
            >
                {premium && (
                    <Box>
                        <Typography sx={{ fontSize: { xs: 10, md: 12 }, color: "#6b7280" }}>Premium</Typography>
                        <Typography sx={{ fontWeight: 700, color: "primary.main", fontSize: { xs: 14, md: 18 } }}>
                            {premium}
                        </Typography>
                    </Box>
                )}
                {dueAmount && (
                    <Box>
                        <Typography sx={{ fontSize: { xs: 10, md: 12 }, color: "#6b7280" }}>Due Amount</Typography>
                        <Typography sx={{ fontWeight: 700, color: brandColors.highlight, fontSize: { xs: 14, md: 18 } }}>
                            {dueAmount}
                        </Typography>
                    </Box>
                )}

                {effective && (
                    <Box>
                        <Typography sx={{ fontSize: { xs: 10, md: 12 }, color: "#6b7280" }}>Effective</Typography>
                        <Typography sx={{ fontWeight: 600, fontSize: { xs: 12, md: 14 } }}>{effective}</Typography>
                    </Box>
                )}
                {expires && (
                    <Box>
                        <Typography sx={{ fontSize: { xs: 10, md: 12 }, color: "#6b7280" }}>Expires</Typography>
                        <Typography sx={{ fontWeight: 600, fontSize: { xs: 12, md: 14 } }}>{expires}</Typography>
                    </Box>
                )}

                {dueDate && (
                    <Box sx={{ gridColumn: { xs: "auto", sm: "1 / span 2" } }}>
                        <Typography sx={{ fontSize: { xs: 10, md: 12 }, color: "#6b7280" }}>Due Date</Typography>
                        <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                            {/* <CalendarTodayOutlinedIcon sx={{ fontSize: 16, color: "#6b7280" }} /> */}
                            <Typography sx={{ fontWeight: 600, fontSize: { xs: 12, md: 14 } }}>{dueDate}</Typography>
                        </Box>
                    </Box>
                )}
            </Box>
            {renderSection(coveragesSection, "",<ShieldOutlinedIcon />)}
            {renderSection(vehiclesSection, "Vehicles", <DirectionsCarFilledOutlinedIcon />)}
            {renderSection(driversSection, "Drivers", <PersonOutlinedIcon />)}
            {renderSection(propertySection, "Property", <HouseOutlinedIcon />)}

{(normalizedCard?.cta?.primary || (normalizedCard?.cta?.secondary || []).length > 0) && (
                    <Box sx={{ borderTop: "1px solid #e5e7eb", pt: 1, mt: 0.5 }}>
                        <Typography
                            sx={{ fontSize: { xs: 10, md: 12 }, color: "#6b7280", mb: 0.5 }}
                        >
                            Quick Actions
                        </Typography>
                        <Box
                            sx={{
                                display: "grid",
                                gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr" },
                                gap: 0.75,
                            }}
                        >
                            {normalizedCard?.cta?.primary && (
                                <Button
                                    size="small"
                                    variant="contained"
                                    disableElevation
                                    onClick={() => handleCtaAction(normalizedCard.cta.primary)}
                                    sx={{
                                        fontSize: { xs: 10, md: 12 },
                                        fontWeight: 600,
                                        textTransform: "none",
                                        borderRadius: 9999,
                                        py: { xs: 0.75, md: 0.9 },
                                    }}
                                >
                                    {normalizedCard.cta.primary.label}
                                </Button>
                            )}
                            {(normalizedCard?.cta?.secondary || []).map((cta: any, idx: number) => (
                                <Button
                                    key={`secondary_${idx}`}
                                    size="small"
                                    variant="outlined"
                                    color="secondary"
                                    onClick={() => handleCtaAction(cta)}
                                    sx={{
                                        fontSize: { xs: 10, md: 12 },
                                        fontWeight: 600,
                                        textTransform: "none",
                                        borderRadius: 9999,
                                        py: { xs: 0.75, md: 0.9 },
                                        // borderColor: (theme) => alpha(theme.palette.secondary.main, 0.6),
                                        // color: "secondary.main",
                                        // "&:hover": {
                                        //     borderColor: "secondary.main",
                                        //     bgcolor: (theme) => alpha(theme.palette.secondary.main, 0.1),
                                        // },
                                    }}
                                >
                                    {cta.label}
                                </Button>
                            ))}
                        </Box>
                    </Box>
                )}

        </Paper>
        </ThemeProvider>
    );
};

export default PolicyDetailsCard;
 